import type { Product } from "./products";

export type MealType = "breakfast" | "lunch" | "dinner" | "snack";

export type MealItem = {
  id: string;
  user_id: number;
  date: string;
  meal_type: MealType;
  product_id: string;
  amount: string;
  product: Product;
  created_at: string;
  updated_at: string;
};

export type MealTotals = {
  kcal: number;
  protein: number;
  fat: number;
  carbs: number;
};

export type DiaryMeal = {
  totals: MealTotals;
  items: MealItem[];
};

export type DiaryDay = {
  date: string;
  user_id: number;
  totals: MealTotals;
  meals: Record<MealType, DiaryMeal>;
};

const BASE = "/api/diary";

async function jsonOrThrow<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(`HTTP ${res.status}: ${text}`);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export async function getDay(date: string, userId: number): Promise<DiaryDay> {
  const params = new URLSearchParams({ date, user_id: String(userId) });
  const res = await fetch(`${BASE}?${params.toString()}`);
  return jsonOrThrow<DiaryDay>(res);
}

export async function addItem(input: {
  date: string;
  meal_type: MealType;
  product_id: string;
  amount: number;
  user_id: number;
}): Promise<MealItem> {
  const res = await fetch(`${BASE}/item`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  return jsonOrThrow<MealItem>(res);
}

export async function updateItem(id: string, amount: number): Promise<MealItem> {
  const res = await fetch(`${BASE}/item/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ amount }),
  });
  return jsonOrThrow<MealItem>(res);
}

export async function deleteItem(id: string): Promise<void> {
  const res = await fetch(`${BASE}/item/${id}`, { method: "DELETE" });
  await jsonOrThrow<void>(res);
}
